/* eslint-disable no-console */
// ServiceWorkerRegistration é uma interface global, não precisa ser importada
export default defineNuxtPlugin(nuxtApp => {
	// Verificar se PWA está habilitada antes de tentar registrar o service worker
	const isPwaEnabled = import.meta.env.PWA_ENABLED === 'true';

	if (!isPwaEnabled) {
		console.log(
			'[PWA] Service Worker desabilitado - PWA_ENABLED não está definida como true'
		);
		return;
	}

	if (import.meta.client && 'serviceWorker' in navigator) {
		// Flag para controlar se já mostramos a notificação de atualização
		let updateNotificationShown = false;
		// ID do intervalo para verificação periódica
		let checkIntervalId: number | null = null;

		// Função para limpar o cache da página inicial especificamente
		function clearRootPageCache() {
			if ('caches' in window) {
				// Tenta acessar todos os caches
				return caches.keys().then(cacheNames => {
					const clearPromises = cacheNames.map(cacheName => {
						// Para cada cache, abre e remove especificamente as entradas da página inicial
						return caches.open(cacheName).then(cache => {
							// Remove '/' e '/index.html' de cada cache
							return Promise.all([
								cache.delete('/'),
								cache.delete('/index.html'),
								// Corrigido: sem especificar o mode
								cache.delete(new Request('/')),
								cache.delete(new Request('/index.html'))
							]);
						});
					});
					return Promise.all(clearPromises);
				});
			}
			return Promise.resolve();
		}

		// Função para limpar todos os caches e recarregar a página
		function clearCachesAndReload() {
			// Limpar cache do navegador usando o Cache API
			if ('caches' in window) {
				caches.keys().then(cacheNames => {
					cacheNames.forEach(cacheName => {
						caches.delete(cacheName);
					});
				});
			}
			// Enviar mensagem para o service worker limpar todos os caches também
			if (navigator.serviceWorker.controller) {
				const messageChannel = new MessageChannel();
				messageChannel.port1.onmessage = event => {
					if (event.data && event.data.success) {
						// Recarregar a página depois de limpar o cache
						window.location.reload();
					}
				};
				navigator.serviceWorker.controller.postMessage(
					{
						type: 'CLEAR_ALL_CACHES'
					},
					[messageChannel.port2]
				);
			} else {
				// Recarregar mesmo se não tivermos um controlador
				window.location.reload();
			}
		}

		// Função para verificar atualizações do Service Worker
		function checkForUpdates() {
			if (navigator.serviceWorker.controller) {
				// console.log('Verificando atualizações do Service Worker...');
				navigator.serviceWorker.getRegistration().then(registration => {
					if (registration) {
						// Força uma verificação manual de atualizações
						registration.update().catch(err => {
							console.error('Erro ao verificar atualizações:', err);
						});
					}
				});
			}
		}

		// Função para iniciar verificações periódicas
		function startPeriodicChecks(intervalSeconds = 60) {
			// Cancelar qualquer intervalo existente
			if (checkIntervalId !== null) {
				clearInterval(checkIntervalId);
			}

			// Configurar novo intervalo
			checkIntervalId = window.setInterval(() => {
				checkForUpdates();
			}, intervalSeconds * 1000);

			// console.log(
			// 	`Verificações periódicas iniciadas (a cada ${intervalSeconds} segundos)`
			// );
		}

		// Função para listar conteúdo do cache para debug
		function listCacheContents() {
			if (navigator.serviceWorker.controller) {
				navigator.serviceWorker.controller.postMessage({
					type: 'LIST_CACHE_CONTENTS'
				});
			} else {
				console.warn('Service Worker não está controlando esta página');
			}
		}

		// Registre o service worker manualmente
		window.addEventListener('load', () => {
			// console.log('Registrando Service Worker...');

			// Limpar cache da página inicial para garantir conteúdo atualizado
			clearRootPageCache().then(() => {
				navigator.serviceWorker
					.register('/sw.js', {
						scope: '/',
						updateViaCache: 'none' // Sempre buscar sw.js da rede, não do cache
					})
					.then((registration: ServiceWorkerRegistration) => {
						// Service Worker registrado com sucesso
						// console.log(
						// 	'Service Worker registrado com sucesso:',
						// 	registration.scope
						// );

						// Iniciar verificações periódicas
						startPeriodicChecks(60); // Verificar a cada 60 segundos

						// Expôr funções para debug
						window.__DEBUG_SW__ = {
							registration,
							clearCache: clearCachesAndReload,
							clearRootCache: clearRootPageCache,
							listCaches: listCacheContents,
							checkNow: checkForUpdates
						};

						// Adicionar handler para atualizações
						registration.addEventListener('updatefound', () => {
							// console.log('Nova versão do Service Worker encontrada!');
							const newWorker = registration.installing;

							if (newWorker) {
								// Monitorar mudanças de estado no novo worker
								newWorker.addEventListener('statechange', () => {
									// console.log(
									// 	'Estado do novo Service Worker:',
									// 	newWorker.state
									// );

									// Quando o novo service worker estiver instalado
									if (
										newWorker.state === 'installed' &&
										navigator.serviceWorker.controller
									) {
										// Novo service worker disponível, mostrar notificação se ainda não mostramos
										if (!updateNotificationShown) {
											updateNotificationShown = true;
											// console.log('Nova versão pronta para uso!');

											// Notificar usuário e recarregar
											clearCachesAndReload();
										}
									}
								});
							}
						});
					})
					.catch((error: Error) => {
						console.error('Falha ao registrar o Service Worker:', error);
					});
			});

			// Lidar com mensagens do service worker
			navigator.serviceWorker.addEventListener(
				'message',
				(event: MessageEvent) => {
					// console.log('Mensagem do Service Worker:', event.data);

					// Se for uma mensagem pedindo para recarregar a página
					if (event.data && event.data.type === 'RELOAD_PAGE') {
						clearCachesAndReload();
					} else if (
						event.data &&
						event.data.type === 'CACHE_CONTENTS_LOGGED'
					) {
						// console.log('Service Worker listou os caches no console');
					} else if (event.data && event.data.type === 'CURRENT_VERSION') {
						// console.log('Versão atual do Service Worker:', event.data.version);
					}
				}
			);
		});

		// Tratar teclas F5 e CTRL+R - Limpar o cache antes de recarregar
		window.addEventListener('keydown', (event: KeyboardEvent) => {
			// Detectar F5 ou CTRL+R
			if (event.key === 'F5' || (event.ctrlKey && event.key === 'r')) {
				// Verificar atualizações antes de recarregar
				checkForUpdates();
			}

			// Detectar Shift+F5 ou CTRL+Shift+R (forçar recarregamento completo)
			if (
				(event.key === 'F5' && event.shiftKey) ||
				(event.ctrlKey && event.key === 'r' && event.shiftKey)
			) {
				// Limpar caches antes de recarregar com hard refresh
				if (navigator.serviceWorker.controller) {
					navigator.serviceWorker.controller.postMessage({
						type: 'CLEAR_ALL_CACHES'
					});
				}
			}
		});

		// Atualizar também quando a aba se torna ativa
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				// Verificar atualizações quando a aba fica visível
				checkForUpdates();
			}
		});

		// Limpar o intervalo quando o plugin é destruído
		nuxtApp.hook('app:suspense:resolve', () => {
			// Quando a app é hidratada, inicia as verificações periódicas
			if (checkIntervalId === null) {
				startPeriodicChecks(60);
			}
		});
	}
});

// Adicionar tipos globais para debug
declare global {
	interface Window {
		__DEBUG_SW__?: {
			registration: ServiceWorkerRegistration;
			clearCache: () => void;
			clearRootCache: () => Promise<any>;
			listCaches: () => void;
			checkNow: () => void;
		};
	}
}
